/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: ClubStreamer
 * File     : ClubStreamer.js
 * Author   : Zachary Dry
 * Created  : 2025-01-27
 * Updated  : 2025-01-27
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component for the Club Streamer with synchronized video viewing,
 *   supporting YouTube, Twitch, and Kick platforms with real-time chat and playlist management.
 *   Exports: ClubStreamer
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/clubs/:clubId/streamer" element={<ClubStreamer />} />
 * -----------------------------------------------------------------------------
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from '@mui/material';
import {
  Send as SendIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/api';

const ClubStreamer = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [messages, setMessages] = useState([]);
  const [playlist, setPlaylist] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [embedStartTime, setEmbedStartTime] = useState(0); // Server master timestamp when embedding (so joiners load at correct position)
  const [masterTimestamp, setMasterTimestamp] = useState(null);
  const [activeViewers, setActiveViewers] = useState(0);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  
  // Cache state with timestamps
  const [cache, setCache] = useState({
    clubData: { data: null, timestamp: 0, ttl: 300000 }, // 5 minutes
    playlist: { data: null, timestamp: 0, ttl: 10000 }, // 10 seconds
    activeUsers: { data: null, timestamp: 0, ttl: 15000 }, // 15 seconds
    sync: { data: null, timestamp: 0, ttl: 2000 } // 2 seconds
  });
  
  const messagesEndRef = useRef(null);
  const playerRef = useRef(null);

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Cache helper functions
  const isCacheValid = (cacheKey) => {
    const now = Date.now();
    const cacheItem = cache[cacheKey];
    return cacheItem && cacheItem.data && (now - cacheItem.timestamp) < cacheItem.ttl;
  };

  const setCacheData = (cacheKey, data) => {
    setCache(prev => ({
      ...prev,
      [cacheKey]: {
        data,
        timestamp: Date.now(),
        ttl: prev[cacheKey].ttl
      }
    }));
  };

  const getCachedData = (cacheKey) => {
    return cache[cacheKey]?.data;
  };

  // Invalidate cache when user makes changes
  // eslint-disable-next-line no-unused-vars
  const invalidateCache = (cacheKey) => {
    setCache(prev => ({
      ...prev,
      [cacheKey]: { data: null, timestamp: 0, ttl: prev[cacheKey].ttl }
    }));
  };

  // Fetch active users list with caching
  const fetchActiveUsers = async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && isCacheValid('activeUsers')) {
      const cachedData = getCachedData('activeUsers');
      setActiveUsers(cachedData.activeUsers || []);
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/active-users`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCacheData('activeUsers', data);
        setActiveUsers(data.activeUsers || []);
      } else {
        console.warn('Failed to fetch active users:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch active users:', error);
    }
  };

  // Update thumbnails for existing videos that might be missing them
  const updateMissingThumbnails = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const playlistData = await response.json();
        const playlist = playlistData.playlist || [];
        
        // Find videos without thumbnails and update them
        const videosNeedingThumbnails = playlist.filter(item => 
          item && item.id && (!item.thumbnail || item.thumbnail.trim() === '')
        );
        
        for (const video of videosNeedingThumbnails) {
          let thumbnail = '';
          
          if (video.platform === 'youtube') {
            thumbnail = `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`;
          } else if (video.platform === 'twitch') {
            thumbnail = 'https://static-cdn.jtvnw.net/ttv-static/404_boxart.jpg';
          } else if (video.platform === 'kick') {
            thumbnail = 'https://kick.com/favicon.ico';
          }
          
          if (thumbnail) {
            try {
              await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist/${video.id}`), {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ thumbnail })
              });
            } catch (error) {
              console.warn(`Failed to update thumbnail for video ${video.id}:`, error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to update missing thumbnails:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);



   // Poll for playlist updates every 5 seconds with caching
   useEffect(() => {
     const pollPlaylist = async (forceRefresh = false) => {
       // Check cache first
       if (!forceRefresh && isCacheValid('playlist')) {
         const cachedData = getCachedData('playlist');
         const filteredPlaylist = (cachedData.playlist || []).filter(item => item && item.id);
         setPlaylist(filteredPlaylist);
         
         // Still check for video changes even with cached data
         const playingVideo = filteredPlaylist.find(item => item.isPlaying);
         if (playingVideo && (!currentVideo || currentVideo.id !== playingVideo.id)) {
           // A new video started playing, switch to it
           setCurrentVideo(playingVideo);
           setCurrentVideoId(playingVideo.id);
           setIsPlaying(true);
         }
         return;
       }

       try {
         const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist`), {
           headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
         });
         
         if (response.ok) {
           const playlistData = await response.json();
           setCacheData('playlist', playlistData);
           const filteredPlaylist = (playlistData.playlist || []).filter(item => item && item.id);
           setPlaylist(filteredPlaylist);
           
           // Check if current video is still playing or if a new video started
           const playingVideo = filteredPlaylist.find(item => item.isPlaying);
           if (playingVideo && (!currentVideo || currentVideo.id !== playingVideo.id)) {
             // New video started — fetch sync first so embed loads at master position
             let syncTimestamp = 0;
             let syncViewers = 0;
             try {
               const syncResponse = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/sync`), {
                 headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
               });
               if (syncResponse.ok) {
                 const syncData = await syncResponse.json();
                 if (syncData && syncData.videoId === playingVideo.id) {
                   syncTimestamp = syncData.timestamp || 0;
                   syncViewers = syncData.activeViewers || 0;
                 }
               }
             } catch (err) {
               console.warn('Failed to get sync data for new video:', err);
             }
             setCurrentVideo(playingVideo);
             setCurrentVideoId(playingVideo.id);
             setEmbedStartTime(syncTimestamp);
             setMasterTimestamp(syncTimestamp);
             setActiveViewers(syncViewers);
             setIsPlaying(true);
           } else if (!playingVideo && currentVideo) {
             // No video is playing anymore - stop playback
             setCurrentVideo(null);
             setCurrentVideoId(null);
             setEmbedStartTime(0);
             setIsPlaying(false);
             setMasterTimestamp(0);
           }
         }
       } catch (error) {
         console.error('Failed to poll playlist:', error);
       }
     };

     // Poll immediately and then every 5 seconds
     pollPlaylist();
     const interval = setInterval(pollPlaylist, 5000);

     return () => clearInterval(interval);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [clubId, currentVideo]);

   // Heartbeat and auto-sync check every 3 seconds
   useEffect(() => {
     // Always send heartbeat when user is on the page, regardless of video state

     const heartbeatAndSync = async () => {
      try {
        // Defensive checks
        if (!clubId) return;

        // Always send heartbeat to indicate user is active in the room
        const heartbeatResponse = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/heartbeat`), {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isActive: true })
        });

        if (!heartbeatResponse.ok) {
          console.warn('Heartbeat failed:', heartbeatResponse.status);
        }

        // Fetch active users list (with caching)
        fetchActiveUsers();

        // Get sync status and auto-sync with caching
        let syncData = null;
        if (isCacheValid('sync')) {
          syncData = getCachedData('sync');
        } else {
          const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/sync`), {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (response.ok) {
            syncData = await response.json();
            setCacheData('sync', syncData);
          }
        }
        
        if (syncData) {
          const data = syncData;
          
           // Handle auto-played next video — refetch playlist immediately so UI shows next video
           if (data.autoPlayedNext && data.videoId) {
             try {
               const plRes = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist`), {
                 headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
               });
               if (plRes.ok) {
                 const plData = await plRes.json();
                 const filtered = (plData.playlist || []).filter(item => item && item.id);
                 setPlaylist(filtered);
                 const nextPlaying = filtered.find(item => item.isPlaying);
                 if (nextPlaying && nextPlaying.id === data.videoId) {
                   setCurrentVideo(nextPlaying);
                   setCurrentVideoId(nextPlaying.id);
                   setEmbedStartTime(data.timestamp ?? 0);
                   setMasterTimestamp(data.timestamp ?? 0);
                   setActiveViewers(data.activeViewers ?? 0);
                   setIsPlaying(true);
                 }
               }
             } catch (e) {
               console.warn('Failed to refetch playlist after autoPlayedNext:', e);
             }
             return;
           }
           
           // Handle playlist ended
           if (data.playlistEnded) {
             if (currentVideo) {
               return;
             }
             setCurrentVideo(null);
             setCurrentVideoId(null);
             setEmbedStartTime(0);
             setIsPlaying(false);
             setMasterTimestamp(0);
             setActiveViewers(0);
             return;
           }
           
           // Handle no video playing case
           if (data && data.noVideoPlaying) {
             setCurrentVideo(null);
             setCurrentVideoId(null);
             setEmbedStartTime(0);
             setIsPlaying(false);
             setMasterTimestamp(0);
             setActiveViewers(data.activeViewers || 0);
             return;
           }
           
           // Handle normal sync for current video
           if (data && data.videoId && currentVideo && data.videoId === currentVideo.id) {
             // Update active viewers count
             const newActiveViewers = data.activeViewers || 0;
             setActiveViewers(newActiveViewers);
             
             // Update master timestamp
             const newTimestamp = data.timestamp || 0;
             setMasterTimestamp(newTimestamp);
             
             // If no active viewers, reset local timestamp to zero
             if (newActiveViewers === 0 && newTimestamp === 0) {
               setMasterTimestamp(0);
             }
             
             // Auto-sync if we're more than 5 seconds off (buffer for latency, buffering, connection speed)
             // Skip auto-sync if masterTimestamp is null (initial sync not complete yet)
         if (masterTimestamp === null) {
           setMasterTimestamp(newTimestamp);
           return;
         }
             
             const timeDiff = Math.abs(newTimestamp - masterTimestamp);
             if (newActiveViewers > 0 && timeDiff > 5) {
               // Only sync if the difference is significant to avoid constant seeking
               if (playerRef.current && playerRef.current.contentWindow && currentVideo && currentVideo.platform) {
                 // Add a small delay to ensure iframe is ready
                 setTimeout(() => {
                   try {
                     // Use postMessage to seek the video player
                     if (currentVideo.platform === 'youtube') {
                       playerRef.current.contentWindow.postMessage(
                         JSON.stringify({
                           event: 'command',
                           func: 'seekTo',
                           args: [newTimestamp, true]
                         }),
                         'https://www.youtube.com'
                       );
                     } else if (currentVideo.platform === 'twitch') {
                       playerRef.current.contentWindow.postMessage(
                         JSON.stringify({
                           event: 'command',
                           func: 'seekTo',
                           args: [newTimestamp]
                         }),
                         'https://player.twitch.tv'
                       );
                     } else if (currentVideo.platform === 'kick') {
                       playerRef.current.contentWindow.postMessage(
                         JSON.stringify({
                           event: 'command',
                           func: 'seekTo',
                           args: [newTimestamp]
                         }),
                         'https://player.kick.com'
                       );
                     }
                   } catch (error) {
                     console.error('Failed to auto-sync video player:', error);
                   }
                 }, 100); // Small delay to ensure iframe is ready
               }
             }
             
             // Update the timestamp
             setMasterTimestamp(newTimestamp);
           } else if (data && data.videoId && currentVideo && data.videoId !== currentVideo.id) {
             // Video ID changed, this means a new video started playing
             // The playlist polling will handle updating the current video
           }
         }
      } catch (error) {
        console.error('Failed to send heartbeat or check sync status:', error);
      }
    };

    // Send initial heartbeat and sync
    heartbeatAndSync();

    // Set up interval for heartbeat and sync (frequent updates)
    const interval = setInterval(heartbeatAndSync, 3000); // Every 3 seconds for seamless sync

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  // Cleanup session when component unmounts or user leaves
  useEffect(() => {
    const cleanupSession = async () => {
      try {
        if (!clubId) return;
        
        await fetch(buildApiUrl(`/api/club-streamer/${clubId}/heartbeat`), {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isActive: false })
        });
      } catch (error) {
        console.error('Failed to cleanup session:', error);
      }
    };

    // Cleanup on page unload
    const handleBeforeUnload = () => {
      cleanupSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanupSession();
    };
   }, [clubId]);

   // Initial sync when video player loads (for new users joining)
   useEffect(() => {
     if (currentVideo && isPlaying && currentVideoId && masterTimestamp > 0) {
       // Wait for player to load, then seek to the current master timestamp
       const initialSeek = () => {
         setTimeout(() => {
           if (playerRef.current && playerRef.current.contentWindow && currentVideo?.platform) {
             try {
               if (currentVideo.platform === 'youtube') {
                 playerRef.current.contentWindow.postMessage(
                   JSON.stringify({
                     event: 'command',
                     func: 'seekTo',
                     args: [masterTimestamp, true]
                   }),
                   'https://www.youtube.com'
                 );
               } else if (currentVideo.platform === 'twitch') {
                 playerRef.current.contentWindow.postMessage(
                   JSON.stringify({
                     event: 'command',
                     func: 'seekTo',
                     args: [masterTimestamp]
                   }),
                   'https://player.twitch.tv'
                 );
               } else if (currentVideo.platform === 'kick') {
                 playerRef.current.contentWindow.postMessage(
                   JSON.stringify({
                     event: 'command',
                     func: 'seekTo',
                     args: [masterTimestamp]
                   }),
                   'https://player.kick.com'
                 );
               }
             } catch (err) {
               console.warn('Failed to initial seek:', err);
             }
           }
         }, 2000); // Give the player time to load
       };
       
       initialSeek();
     }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentVideoId]); // Only run when video changes, not when timestamp changes

   // Suppress YouTube analytics errors (harmless but noisy)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      // Filter out YouTube analytics errors
      if (args[0] && typeof args[0] === 'string' && 
          (args[0].includes('youtubei/v1/log_event') || 
           args[0].includes('ERR_BLOCKED_BY_CLIENT'))) {
        return; // Suppress these errors
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  // Load club data and initial messages/playlist
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load active users
        fetchActiveUsers();
        
        // Update thumbnails for existing videos that might be missing them
        updateMissingThumbnails();
        
        // Load club info with caching
        let clubData = null;
        if (isCacheValid('clubData')) {
          clubData = getCachedData('clubData');
        } else {
          const clubResponse = await fetch(buildApiUrl(`/api/clubs/${clubId}`), {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (clubResponse.ok) {
            clubData = await clubResponse.json();
            setCacheData('clubData', clubData);
          }
        }
        
        if (!clubData) {
          setError('Failed to load club data');
          return;
        }
        
        setClub(clubData);
        
        // Load chat messages
        const messagesResponse = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/chat`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (messagesResponse.ok) {
          const messagesData = await messagesResponse.json();
          const filteredMessages = (messagesData.messages || []).filter(message => message && message.id);
          setMessages(filteredMessages);
        }
        
        // Load playlist
        const playlistResponse = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (playlistResponse.ok) {
          const playlistData = await playlistResponse.json();
          const filteredPlaylist = (playlistData.playlist || []).filter(item => item && item.id);
          setPlaylist(filteredPlaylist);
          
          // Set current video if one is playing — fetch sync first so we can embed at master position
          const playingVideo = filteredPlaylist.find(item => item.isPlaying);
          if (playingVideo) {
            let syncTimestamp = 0;
            let syncViewers = 0;
            try {
              const syncResponse = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/sync`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              if (syncResponse.ok) {
                const syncData = await syncResponse.json();
                if (syncData && syncData.noVideoPlaying) {
                  setMasterTimestamp(0);
                  setActiveViewers(syncData.activeViewers || 0);
                  setCurrentVideo(playingVideo);
                  setCurrentVideoId(playingVideo.id);
                  setEmbedStartTime(0);
                  setIsPlaying(true);
                  return;
                }
                if (syncData && syncData.videoId === playingVideo.id) {
                  syncTimestamp = syncData.timestamp || 0;
                  syncViewers = syncData.activeViewers || 0;
                }
              }
            } catch (err) {
              console.warn('Failed to get initial sync timestamp:', err);
            }
            setCurrentVideo(playingVideo);
            setCurrentVideoId(playingVideo.id);
            setEmbedStartTime(syncTimestamp);
            setMasterTimestamp(syncTimestamp);
            setActiveViewers(syncViewers);
            setIsPlaying(true);
            // Fallback: seek via postMessage once iframe is ready (for Twitch/Kick or if embed start param missed)
            if (syncTimestamp > 0 && playingVideo.platform !== 'youtube') {
              const attemptSeek = (retryCount = 0) => {
                if (playerRef.current?.contentWindow && playingVideo.platform) {
                  try {
                    if (playingVideo.platform === 'twitch') {
                      playerRef.current.contentWindow.postMessage(
                        JSON.stringify({ event: 'command', func: 'seekTo', args: [syncTimestamp] }),
                        'https://player.twitch.tv'
                      );
                    } else if (playingVideo.platform === 'kick') {
                      playerRef.current.contentWindow.postMessage(
                        JSON.stringify({ event: 'command', func: 'seekTo', args: [syncTimestamp] }),
                        'https://player.kick.com'
                      );
                    }
                  } catch (e) { console.error('Seek fallback error:', e); }
                } else if (retryCount < 10) {
                  setTimeout(() => attemptSeek(retryCount + 1), 500);
                }
              };
              setTimeout(() => attemptSeek(), 800);
            }
          }
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (clubId) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  // Send chat message
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/chat`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message: newMessage.trim() })
      });
      
      if (response.ok) {
        const messageData = await response.json();
        if (messageData.message && messageData.message.id) {
          setMessages(prev => [...prev, messageData.message]);
        }
        setNewMessage('');
      } else {
        const errorData = await response.json();
        
        // Handle spam detection errors with specific messaging
        if (response.status === 429) {
          if (errorData.reasons && errorData.reasons.includes('rate_limit_exceeded')) {
            alert(`Rate limit exceeded: ${errorData.error}`);
          } else if (errorData.reasons && errorData.reasons.includes('message_similarity')) {
            alert('Message too similar to recent messages. Please try a different message.');
          } else if (errorData.reasons && errorData.reasons.includes('spam_patterns_detected')) {
            alert('Message contains spam patterns. Please revise your message.');
          } else if (errorData.reasons && errorData.reasons.includes('high_spam_score')) {
            alert('Account flagged for spam. Please contact an administrator.');
          } else {
            alert(`Message blocked: ${errorData.error}`);
          }
        } else {
          alert(errorData.error || 'Failed to send message');
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Failed to send message');
    }
  };

  // Delete chat message
  const deleteMessage = async (messageId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/chat/${messageId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        setMessages(prev => prev.filter(msg => msg && msg.id && msg.id !== messageId));
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  // Add video to playlist
  const addVideo = async () => {
    if (!newVideoUrl.trim()) return;
    
    try {
      // Extract video info from URL
      const videoInfo = extractVideoInfo(newVideoUrl);
      if (!videoInfo) {
        alert('Please enter a valid YouTube, Twitch, or Kick URL');
        return;
      }
      
       // Get video info (simplified - in production you'd want to use platform APIs)
       const title = `Video ${videoInfo.videoId}`;
       let thumbnail = '';
       let duration = null;
       
       if (videoInfo.platform === 'youtube') {
         thumbnail = `https://img.youtube.com/vi/${videoInfo.videoId}/mqdefault.jpg`;
         // For YouTube, we could use the YouTube API to get duration, but for now we'll leave it null
         // duration = await getYouTubeVideoDuration(videoInfo.videoId);
       } else if (videoInfo.platform === 'twitch') {
         // Twitch doesn't have a simple thumbnail API, so we'll use a placeholder
         thumbnail = `https://static-cdn.jtvnw.net/ttv-static/404_boxart.jpg`;
       } else if (videoInfo.platform === 'kick') {
         // Kick doesn't have a simple thumbnail API, so we'll use a placeholder
         thumbnail = `https://kick.com/favicon.ico`;
       }
      
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title,
          videoId: videoInfo.videoId,
          platform: videoInfo.platform,
          thumbnail,
          duration
        })
      });
      
       if (response.ok) {
         // Don't update local state here - let the polling handle it
         // This ensures all users get the update at the same time
         setNewVideoUrl('');
         setAddVideoOpen(false);
       } else {
         const errorData = await response.json();
         alert(errorData.error || 'Failed to add video');
       }
    } catch (err) {
      console.error('Failed to add video:', err);
    }
  };

  // Extract video ID and platform from URL
  const extractVideoInfo = (url) => {
    // YouTube
    const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const youtubeMatch = url.match(youtubeRegExp);
    if (youtubeMatch && youtubeMatch[2].length === 11) {
      return { platform: 'youtube', videoId: youtubeMatch[2] };
    }

    // Twitch
    const twitchRegExp = /(?:twitch\.tv\/videos\/(\d+)|twitch\.tv\/([^/?]+))/;
    const twitchMatch = url.match(twitchRegExp);
    if (twitchMatch) {
      return { platform: 'twitch', videoId: twitchMatch[1] || twitchMatch[2] };
    }

    // Kick
    const kickRegExp = /kick\.com\/([^/?]+)/;
    const kickMatch = url.match(kickRegExp);
    if (kickMatch) {
      return { platform: 'kick', videoId: kickMatch[1] };
    }

    return null;
  };

   // Play video (Host only)
   const playVideo = async (item) => {
     try {
       const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist/${item.id}/playing`), {
         method: 'PUT',
         headers: { 
           'Authorization': `Bearer ${localStorage.getItem('token')}`,
           'Content-Type': 'application/json'
         },
         body: JSON.stringify({ timestamp: 0 }) // Start from beginning
       });
       
       if (response.ok) {
         await response.json();
         // Don't update local state here - let the polling handle it
         // This ensures all users get the update at the same time
         
         // Trigger immediate playlist poll to update the UI faster
         setTimeout(() => {
           const pollPlaylist = async () => {
             try {
               const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist`), {
                 headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
               });
               
               if (response.ok) {
                 const playlistData = await response.json();
                 const filteredPlaylist = (playlistData.playlist || []).filter(item => item && item.id);
                 setPlaylist(filteredPlaylist);
                 
                 // Check if current video is still playing or if a new video started
                 const playingVideo = filteredPlaylist.find(item => item.isPlaying);
                 if (playingVideo && (!currentVideo || currentVideo.id !== playingVideo.id)) {
                   // A new video started playing, switch to it
                   setCurrentVideo(playingVideo);
                   setCurrentVideoId(playingVideo.id);
                   setIsPlaying(true);
                 }
               }
             } catch (error) {
               console.error('Failed to poll playlist after play:', error);
             }
           };
           pollPlaylist();
         }, 500); // Small delay to ensure backend has processed the request
       } else {
         const errorData = await response.json();
         alert(errorData.error || 'Failed to play video');
       }
     } catch (err) {
       console.error('Failed to play video:', err);
       alert('Failed to play video');
     }
   };

  // Remove video from playlist (Host only)
  const removeVideo = async (itemId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/playlist/${itemId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        // Don't update local state here - let the polling handle it
        // This ensures all users get the update at the same time
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to remove video');
      }
    } catch (err) {
      console.error('Failed to remove video:', err);
      alert('Failed to remove video');
    }
  };



  // Update master timestamp (Club Hosts only)
  // eslint-disable-next-line no-unused-vars
  const updateMasterTimestamp = async (timestamp) => {
    // Defensive checks
    if (!currentVideo || !currentVideo.id) return;
    if (!clubId) return;
    if (!timestamp || isNaN(timestamp) || timestamp < 0) return;
    
    // Check if user is Club Host
    const isClubHost = user?.role === 'owner' || user?.role === 'admin' || (club?.owner?.id && user?.id && club.owner.id === user.id);
    if (!isClubHost) return;
    
    try {
      const response = await fetch(buildApiUrl(`/api/club-streamer/${clubId}/sync`), {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          timestamp: Math.floor(timestamp),
          videoId: currentVideo.id 
        })
      });
      
      if (response.ok) {
        setMasterTimestamp(Math.floor(timestamp));
      } else {
        console.warn('Failed to update master timestamp:', response.status);
      }
    } catch (err) {
      console.error('Failed to update master timestamp:', err);
    }
  };

  // Check if user can delete messages (admin/owner)
  const canDeleteMessage = (message) => {
    if (!user || !message) return false;
    if (user.role === 'owner' || user.role === 'admin') return true;
    if (message.userId && user.id && message.userId === user.id) return true;
    // Check if user is club admin/owner (would need club membership data)
    return false;
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/clubs')} sx={{ mt: 2 }}>
          Back to Clubs
        </Button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          {club?.name} Streamer
        </Typography>
        <Button onClick={() => navigate(`/clubs/${clubId}`)}>
          Back to Club
        </Button>
      </Box>

      <Box sx={{ 
        position: 'relative', 
        height: 'calc(100vh - 64px)', // Full height minus header
        display: 'flex',
        width: { xs: '100vw', sm: 'calc(100vw - 1128px)' }, // Full width on mobile, subtract chat drawer width on desktop
        marginLeft: '-16px',
        maxWidth: { xs: '100vw', sm: 'calc(100vw - 1128px)' } // Ensure it doesn't exceed available space
      }}>
         {/* Main Content Area - Takes up remaining space */}
         <Box sx={{ 
           flex: 1,  
           display: 'flex', 
           flexDirection: 'column',
           width: '100%', 
           minWidth: 0,
           maxWidth: '100%'
         }}>
          {/* Video Player */}
          <Box sx={{ flex: 2, minHeight: '600px', display: 'flex', flexDirection: 'column', p: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {currentVideo ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">
                    {currentVideo.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <Chip 
                       label={`${activeViewers} Active Viewer${activeViewers !== 1 ? 's' : ''}`}
                       color={activeViewers > 0 ? 'primary' : 'default'}
                       size="small"
                     />
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (playerRef.current && playerRef.current.contentWindow && currentVideo?.platform && masterTimestamp > 0) {
                        try {
                          if (currentVideo.platform === 'youtube') {
                            playerRef.current.contentWindow.postMessage(
                              JSON.stringify({
                                event: 'command',
                                func: 'seekTo',
                                args: [masterTimestamp, true]
                              }),
                              'https://www.youtube.com'
                            );
                          } else if (currentVideo.platform === 'twitch') {
                            playerRef.current.contentWindow.postMessage(
                              JSON.stringify({
                                event: 'command',
                                func: 'seekTo',
                                args: [masterTimestamp]
                              }),
                              'https://player.twitch.tv'
                            );
                          } else if (currentVideo.platform === 'kick') {
                            playerRef.current.contentWindow.postMessage(
                              JSON.stringify({
                                event: 'command',
                                func: 'seekTo',
                                args: [masterTimestamp]
                              }),
                              'https://player.kick.com'
                            );
                          }
                        } catch (err) {
                          console.warn('Failed to manual sync:', err);
                        }
                      }
                    }}
                  >
                    Sync
                  </Button>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    width: '100%',
                    backgroundColor: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                   {currentVideo.platform === 'youtube' && (
                     <iframe
                       key={`youtube-${currentVideoId || currentVideo?.id || 'default'}`}
                       ref={playerRef}
                       width="100%"
                       height="100%"
                       src={`https://www.youtube.com/embed/${currentVideo.videoId}?autoplay=1&controls=1&enablejsapi=1&origin=${window.location.origin}&rel=0&modestbranding=1${embedStartTime > 0 ? `&start=${Math.floor(embedStartTime)}` : ''}`}
                       title={currentVideo.title}
                       frameBorder="0"
                       allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                       allowFullScreen
                       style={{ border: 'none' }}
                     />
                   )}
                  {currentVideo.platform === 'twitch' && (
                    <iframe
                      key={`twitch-${currentVideoId || currentVideo?.id || 'default'}`}
                      ref={playerRef}
                      width="100%"
                      height="100%"
                      src={`https://player.twitch.tv/?video=${currentVideo.videoId}&parent=${window.location.hostname}&autoplay=true${embedStartTime > 0 ? `&time=${Math.floor(embedStartTime / 3600)}h${Math.floor((embedStartTime % 3600) / 60)}m${Math.floor(embedStartTime % 60)}s` : ''}`}
                      title={currentVideo.title}
                      frameBorder="0"
                      allowFullScreen
                      style={{ border: 'none' }}
                    />
                  )}
                  {currentVideo.platform === 'kick' && (
                    <iframe
                      key={`kick-${currentVideoId || currentVideo?.id || 'default'}`}
                      ref={playerRef}
                      width="100%"
                      height="100%"
                      src={`https://player.kick.com/${currentVideo.videoId}?autoplay=true`}
                      title={currentVideo.title}
                      frameBorder="0"
                      allowFullScreen
                      style={{ border: 'none' }}
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                flexDirection="column"
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No video playing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a video from the playlist to start streaming
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    label={`${activeViewers} Active Viewer${activeViewers !== 1 ? 's' : ''}`}
                    color={activeViewers > 0 ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Playlist */}
          <Box sx={{ p: 2, maxHeight: '300px', overflow: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h3">
                Playlist
              </Typography>
              {(user?.role === 'owner' || user?.role === 'admin' || (club?.owner?.id && user?.id && club.owner.id === user.id)) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddVideoOpen(true)}
                >
                  Add Video
                </Button>
              )}
            </Box>
            
            <List>
              {playlist.filter(item => item && item.id).map((item, index) => (
                <ListItem key={item.id} divider>
                  <Avatar
                    src={item.thumbnail || (item.platform === 'youtube' ? `https://img.youtube.com/vi/${item.videoId}/mqdefault.jpg` : 
                                          item.platform === 'twitch' ? 'https://static-cdn.jtvnw.net/ttv-static/404_boxart.jpg' :
                                          item.platform === 'kick' ? 'https://kick.com/favicon.ico' : '')}
                    variant="rounded"
                    sx={{ width: 60, height: 45, mr: 2 }}
                    onError={(e) => {
                      // Fallback to platform-specific default thumbnails
                      if (item.platform === 'youtube') {
                        e.target.src = `https://img.youtube.com/vi/${item.videoId}/mqdefault.jpg`;
                      } else if (item.platform === 'twitch') {
                        e.target.src = 'https://static-cdn.jtvnw.net/ttv-static/404_boxart.jpg';
                      } else if (item.platform === 'kick') {
                        e.target.src = 'https://kick.com/favicon.ico';
                      } else {
                        e.target.src = '/icons/favicon-32x32.png'; // App fallback
                      }
                    }}
                  />
                  <ListItemText
                    primary={item.title}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Added by {item.adder?.displayName || item.adder?.username || 'Unknown User'}
                        </Typography>
                        <Chip 
                          label={item.platform?.toUpperCase() || 'YOUTUBE'} 
                          size="small" 
                          color={item.platform === 'youtube' ? 'error' : item.platform === 'twitch' ? 'secondary' : 'primary'}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                   <ListItemSecondaryAction>
                     <Box display="flex" gap={1}>
                       {item.isPlaying && (
                         <Chip label="Playing" color="primary" size="small" />
                       )}
                       {/* Only hosts can play videos */}
                       {(user?.role === 'owner' || user?.role === 'admin' || (club?.owner?.id && user?.id && club.owner.id === user.id)) && (
                         <IconButton
                           size="small"
                           onClick={() => playVideo(item)}
                           disabled={item.isPlaying}
                           title="Play video (Host only)"
                         >
                           <PlayIcon />
                         </IconButton>
                       )}
                       {/* Only hosts can remove videos */}
                       {(user?.role === 'owner' || user?.role === 'admin' || (club?.owner?.id && user?.id && club.owner.id === user.id)) && (
                         <IconButton
                           size="small"
                           onClick={() => removeVideo(item.id)}
                           color="error"
                           title="Remove video (Host only)"
                         >
                           <DeleteIcon />
                         </IconButton>
                       )}
                     </Box>
                   </ListItemSecondaryAction>
                </ListItem>
              ))}
              {playlist.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No videos in playlist"
                    secondary="Add a YouTube video to get started"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Box>

        {/* Chat Drawer - Fixed position at far right */}
        <Box sx={{ 
          position: 'fixed',
          right: 0,
          top: '64px', // Start just below header
          width: { xs: '100vw', sm: '500px' }, // Full width on mobile, fixed width on larger screens
          height: 'calc(100vh - 64px)', // Full height minus header
          p: 2, 
          display: { xs: 'none', sm: 'flex' }, // Hidden on mobile, visible on larger screens
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          zIndex: 1000
        }}>
          <Typography variant="h6" gutterBottom>
            Chat
          </Typography>
          
          {/* Active Users List */}
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            backgroundColor: 'rgba(0, 0, 0, 0.1)', 
            borderRadius: 1,
            maxHeight: '120px',
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Typography variant="caption" sx={{ 
              mb: 1, 
              display: 'block', 
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}>
              Active Users ({activeUsers.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activeUsers.filter(user => user && user.id).map((user) => {
                // Determine user role and club level for better color coding
                const isClubOwner = club?.owner?.id === user.id;
                const isPlatformOwner = user.role === 'owner';
                const isPlatformAdmin = user.role === 'admin';
                
                let chipColor = '#666'; // Default gray for regular users
                let chipLabel = user.displayName || user.username || 'Unknown';
                
                if (isPlatformOwner) {
                  chipColor = '#d32f2f'; // Red for platform owners
                  chipLabel = `👑 ${chipLabel}`;
                } else if (isPlatformAdmin) {
                  chipColor = '#1976d2'; // Blue for platform admins
                  chipLabel = `🛡️ ${chipLabel}`;
                } else if (isClubOwner) {
                  chipColor = '#f57c00'; // Orange for club owners
                  chipLabel = `🏠 ${chipLabel}`;
                } else {
                  chipColor = '#388e3c'; // Green for regular club members
                }
                
                return (
                  <Chip
                    key={user.id}
                    label={chipLabel}
                    size="small"
                    sx={{
                      backgroundColor: chipColor,
                      color: 'white',
                      fontSize: '0.7rem',
                      height: '22px',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1.2,
                        py: 0.5
                      },
                      '&:hover': {
                        backgroundColor: chipColor,
                        opacity: 0.8
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          
          <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
            <List dense>
              {messages.filter(message => message && message.id && message.user).map((message) => (
                <ListItem key={message.id} sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {message.user?.displayName || message.user?.username || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {message.createdAt ? new Date(message.createdAt).toLocaleTimeString() : 'Unknown Time'}
                        </Typography>
                      </Box>
                    }
                    secondary={message.message || ''}
                  />
                  {canDeleteMessage(message) && (
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => deleteMessage(message.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
              <div ref={messagesEndRef} />
            </List>
          </Box>
          
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            />
            <Button
              variant="contained"
              onClick={sendMessage}
              disabled={!newMessage.trim()}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Add Video Dialog */}
      <Dialog open={addVideoOpen} onClose={() => setAddVideoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Video to Playlist</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Video URL"
            placeholder="YouTube, Twitch, or Kick URL..."
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            helperText="Supported: YouTube, Twitch TV, Kick"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVideoOpen(false)}>Cancel</Button>
          <Button onClick={addVideo} variant="contained" disabled={!newVideoUrl.trim()}>
            Add Video
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubStreamer;
